import type { BlogCardProps } from '../components/BlogCard'
import type { BlogMeta } from '../components/BlogMetas'
import type { InnerSidebarProps } from '../components/InnerSidebar'
import { BLOG_AUTHOR, type BlogPost } from './types'

/**
 * Everything derived from a list of posts: the cards, the meta rows, the sidebar and the
 * slug lookup.
 *
 * **Derived, never authored.** Each of these was a hand-maintained module in `app_website`
 * before the blog became shared, and the sidebar in particular is the one that would rot:
 * a recent-posts list written by hand is a list of posts that used to exist. Everything
 * here is a function of the posts the app was given, so an app with three posts and an app
 * with twelve both get a correct sidebar from the same line.
 *
 * Nothing here reads the filesystem or the virtual module — the app passes its posts in.
 * That is what lets `app_design` stay free of `virtual:blog-posts`, which is a bundler
 * concept the design system has no business knowing about.
 */

export const metasFor = (post: BlogPost): BlogMeta[] => [
  { icon: 'account', text: BLOG_AUTHOR },
  { icon: 'price-tag', text: post.category },
]

const card = (post: BlogPost, variant: NonNullable<BlogCardProps['variant']>): BlogCardProps => ({
  image: post.image,
  imageAlt: post.imageAlt,
  title: post.title,
  to: `/blog/${post.slug}`,
  metas: metasFor(post),
  variant,
  ...(variant === 'grid' && post.date ? { date: post.date } : {}),
  ...(variant === 'grid' ? {} : { excerpt: post.excerpt }),
})

/** Grid cards, for a home page's blog band. */
export const blogGridCards = (posts: BlogPost[]): BlogCardProps[] =>
  posts.map((post) => card(post, 'grid'))

/**
 * The index's cards — every post as a compact row.
 *
 * Every card was the `inner` variant until 2026-09-19: a 890x600 picture and a 50px title
 * each, so a dozen posts were a dozen screens and the index could not be scanned. The first
 * pass that day kept the newest as a lead card at that size; it was still most of a screen
 * on its own, so **there is no lead card**. See `BlogCard`'s `compact`.
 */
export const blogRowCards = (posts: BlogPost[]): BlogCardProps[] =>
  posts.map((post) => card(post, 'compact'))

/**
 * The post a slug names, or `undefined`.
 *
 * It returned `POSTS[0]` on a miss while one app had a blog, which renders somebody else's
 * article under the address you typed. Four apps now carry a *subset* of one library, so a
 * miss is the ordinary case — a link to a Garage post pasted into the resort's site — and
 * the page has to be able to say so. `BlogArticle` renders its not-found state.
 */
export const postBySlug = (posts: BlogPost[], slug?: string): BlogPost | undefined =>
  slug === undefined ? undefined : posts.find((post) => post.slug === slug)

/**
 * The `.ul-inner-sidebar`'s content, derived from the posts this app carries.
 *
 * Categories are counted rather than listed, recent posts are the posts, and the tag cloud
 * is every tag that appears. A sidebar that named a category with no posts behind it would
 * be a link to an empty list, and on a per-app blog that is exactly what a hand-written one
 * would become.
 *
 * `to` on a category and a tag is `/blog` rather than a filtered URL: the index does not
 * filter yet. They are navigation back to the list, not a promise of a facet — and when the
 * index does filter, this is the one place that has to change.
 */
export const blogSidebar = (posts: BlogPost[]): Omit<InnerSidebarProps, 'onSearch'> => {
  const counts = posts.reduce<Record<string, number>>((acc, post) => {
    acc[post.category] = (acc[post.category] ?? 0) + 1
    return acc
  }, {})

  return {
    categories: Object.entries(counts).map(([label, count]) => ({
      label,
      count: `(${String(count).padStart(2, '0')})`,
      to: '/blog',
    })),
    posts: posts.map((post) => ({
      image: post.image,
      title: post.title,
      // An evergreen post carries no date, and the byline slot still has to say something.
      // The category is what it is about, which is more use than a made-up date.
      date: post.published ?? post.category,
      to: `/blog/${post.slug}`,
    })),
    tags: [...new Set(posts.flatMap((post) => post.tags))].map((label) => ({ label, to: '/blog' })),
  }
}
